import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import { useEffect, useState } from "react";
import { formatMoneyVN } from "@app/utils/util";
import useObservable from "@core/hooks/use-observable.hook";
import AccountService from "@app/services/http/account.service";
import { Account, ComputerConfig, OrderVps } from "@app/types";
import { VPS_OS, VPS_Status } from "@app/constants";
import { useT } from "@app/contexts/preferences.context";
import clsx from "clsx";
import dayjs from "dayjs";
import { IcClock, IcMinus, IcPlus, IcServer, IcWallet, IcX } from "../icons";
import { btn, btnLg, btnPrimary, iconBtn } from "../ui";

interface ExtendRentalConfigModalProps {
  /** monthly_rental_price của gói, như bản cũ */
  price: number;
  /** Đơn VPS đang gia hạn (để hiện tên gói và ngày kết thúc) */
  order?: OrderVps;
}

/**
 * Gia hạn VPS. Trả về { rental_period } như bản cũ để gọi OrderVpsService.extendOrderVps.
 * Thiếu tiền thì trả về { topup: số tiền còn thiếu } để trang mở Nạp tiền (như bản thiết kế).
 */
function ExtendRentalConfigModal({ portalDialogRef, portalData }: PortalDialogProps<ExtendRentalConfigModalProps>) {
  const t = useT();
  const [month, setMonthState] = useState(1);
  const [currentAccount, setCurrentAccount] = useState<Account>();

  const { subscribeOnce } = useObservable();

  useEffect(() => {
    subscribeOnce(AccountService.getMyInfo(), (data) => {
      if (data) {
        setCurrentAccount(data);
      }
    });
  }, []);

  const price = portalData?.price || 0;
  const order = portalData?.order;
  const config = order?.vps?.config as ComputerConfig | undefined;
  // tối đa 12 tháng như bản thiết kế
  const setMonth = (n: number) => setMonthState(Math.min(12, Math.max(1, Math.floor(n) || 1)));

  // Giá giống bản cũ: số tháng × (giá - giá × discount_vps / 100)
  const discount = currentAccount?.discount_vps || 0;
  const total = month * (price - (price * discount) / 100);
  const remain = (currentAccount?.balance || 0) - total;

  // Kết thúc hiện tại giống cột End Time cũ: time_start + rental_period tháng
  const end =
    order &&
    (order.status === VPS_Status.END ? dayjs(order.end_time) : dayjs(order.time_start).add(order.rental_period, "month"));
  const close = () => portalDialogRef.close();
  const row = "flex items-center justify-between gap-3 [&>b]:whitespace-nowrap";

  return (
    <Modal onCancel={close} buttonCancelInChildren={false}>
      <div className="flex w-[min(480px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start justify-between gap-3 border-b border-line px-6 pb-4 pt-5">
          <div className="min-w-0">
            <h2 className="text-[19px] font-bold">{t("Gia hạn VPS")}</h2>
            {order && <p className="m-0 mt-0.5 truncate text-[13px] text-muted">VPS_{order.vps?._id}</p>}
          </div>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8")} aria-label={t("Đóng")} onClick={close}>
            <IcX className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          {config && (
            <div className="flex items-center gap-3 rounded-xl bg-surface-2 px-3 py-2.5">
              <span className="grid h-11 w-11 flex-none place-items-center rounded-[10px] bg-surface text-brand">
                <IcServer className="h-6 w-6" />
              </span>
              <div className="min-w-0">
                <b className="block text-base">{config.config_name}</b>
                <span className="text-[13px] text-muted">
                  {formatMoneyVN(price)} VND/{t("tháng")} · {order?.vps?.os === VPS_OS.UBUNTU ? "Ubuntu" : "Windows"}
                </span>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold text-muted">{t("Số tháng gia hạn")}</span>
            <div className="inline-flex items-center overflow-hidden rounded-[10px] border border-line">
              <button type="button" className="grid h-10 w-10 place-items-center text-muted hover:bg-surface-2" onClick={() => setMonth(month - 1)}>
                <IcMinus className="h-4 w-4" />
              </button>
              <input
                type="number"
                min={1}
                max={12}
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="h-10 w-12 border-x border-line bg-transparent text-center font-semibold tabular-nums outline-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button type="button" className="grid h-10 w-10 place-items-center text-muted hover:bg-surface-2" onClick={() => setMonth(month + 1)}>
                <IcPlus className="h-4 w-4" />
              </button>
            </div>
            <div className="ml-auto flex gap-1.5">
              {[1, 3, 6].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setMonth(n)}
                  className={clsx(
                    "min-w-[36px] rounded-lg border px-2 py-1 text-[13px]",
                    month === n ? "border-brand text-brand" : "border-line text-muted"
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 rounded-xl bg-surface-2 px-4 py-3 text-sm tabular-nums">
            <div className={row}>
              <span className="text-muted">{t("Thời gian thêm")}</span>
              <b>
                {month} {t("tháng")} ({month * 720} {t("giờ")})
              </b>
            </div>
            {end && (
              <>
                <div className={row}>
                  <span className="text-muted">{t("Kết thúc hiện tại")}</span>
                  <b>{end.format("DD/MM/YYYY HH:mm")}</b>
                </div>
                <div className={row}>
                  <span className="text-muted">{t("Kết thúc sau gia hạn")}</span>
                  <b className="text-brand">{end.add(month, "month").format("DD/MM/YYYY HH:mm")}</b>
                </div>
              </>
            )}
            <div className={clsx(row, "border-t border-line pt-2")}>
              <b>{t("Thành tiền")}</b>
              <span className="flex items-baseline gap-2">
                {discount > 0 && <s className="text-xs text-faint">{formatMoneyVN(month * price)}</s>}
                <strong className="text-xl text-brand">{formatMoneyVN(total)} VND</strong>
              </span>
            </div>
          </div>

          {currentAccount && remain < 0 ? (
            <button
              type="button"
              className={clsx(btn, btnPrimary, btnLg, "w-full")}
              onClick={() => portalDialogRef.close({ topup: -remain })}
            >
              <IcWallet className="h-[18px] w-[18px]" />
              {t("Nạp thêm")} {formatMoneyVN(Math.ceil(-remain))} VND
            </button>
          ) : (
            <>
              <button
                type="button"
                className={clsx(btn, btnPrimary, btnLg, "w-full")}
                onClick={() => portalDialogRef.close({ rental_period: month })}
              >
                <IcClock className="h-[18px] w-[18px]" />
                {t("Gia hạn")} · {formatMoneyVN(total)}đ
              </button>

              {currentAccount && (
                <p className="m-0 text-center text-[13px] text-muted">
                  {t("Số dư còn lại sau gia hạn")}: <b className="text-fg">{formatMoneyVN(remain)} VND</b>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ExtendRentalConfigModal;
